import React, { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import type { TrainingPlan, TrainingSession } from '../../types';

export interface WorkoutHistoryListProps {
  sessions: TrainingSession[];
  plans: TrainingPlan[];
  loading: boolean;
  onBack: () => void;
}

function dayTitleForSession(plan: TrainingPlan | undefined, dayId: string): string {
  if (!plan) return dayId.slice(0, 8);
  const d = plan.exercises.days.find((x) => x.dayId === dayId);
  return d?.title ?? 'Day';
}

const WorkoutHistoryList: React.FC<WorkoutHistoryListProps> = ({
  sessions,
  plans,
  loading,
  onBack,
}) => {
  const planById = useMemo(() => {
    const m = new Map<string, TrainingPlan>();
    plans.forEach((p) => m.set(p.id, p));
    return m;
  }, [plans]);

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-theme-primary transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-theme-primary" />
          </button>
          <h2 className="text-xl font-bold text-theme-primary">Workout history</h2>
        </div>

        {loading ? (
          <p className="text-center text-theme-secondary py-12">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="text-center text-theme-secondary py-12">No completed workouts yet.</p>
        ) : (
          <ul className="space-y-3">
            {sessions.map((s) => {
              const plan = planById.get(s.plan_id);
              const dayTitle = dayTitleForSession(plan, s.day_id);
              const planName = plan?.name ?? 'Deleted plan';
              const start = new Date(s.started_at);
              const end = s.ended_at ? new Date(s.ended_at) : null;
              const durationMin =
                end != null
                  ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000))
                  : null;

              return (
                <li
                  key={s.id}
                  className="bg-theme-primary rounded-lg border border-theme-primary p-4"
                >
                  <p className="font-semibold text-theme-primary">{planName}</p>
                  <p className="text-sm text-theme-secondary mt-1">
                    {dayTitle} · {format(start, 'MMM d, yyyy · HH:mm')}
                  </p>
                  {durationMin != null && (
                    <p className="text-xs text-theme-secondary mt-2">~{durationMin} min</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WorkoutHistoryList;
